/* global debounce removeTrapFocus trapFocus throttle */
/**
 *  @description <media-carousel>
 */
class MediaCarousel extends HTMLElement {
  constructor() {
    super()
    this.elements = {
      liveRegion: this.querySelector('[id^="CarouselStatus"]'),
      viewer: this.querySelector('[id^="CarouselViewer"]'),
      thumbnails: this.querySelector('[id^="ThumbnailViewer"]')
    }
    this.mql = window.matchMedia('(min-width: 767px)')

    if (!this.elements.thumbnails || !this.mql.matches) return

    //- save event handler reference for cleanup
    this.boundOnSlideChanged = debounce(this.onSlideChanged.bind(this), 100)
    this.thumbnailClickHandlers = []

    //- when theme switch, set current active thumbnail
    this.elements.viewer.addEventListener('carouselSlideChanged', this.boundOnSlideChanged)
    this.elements.thumbnails.querySelector('button[name="next"]')?.addEventListener('click', () => {
      this.elements.viewer.next()
    })
    this.elements.thumbnails.querySelector('button[name="previous"]')?.addEventListener('click', () => {
      this.elements.viewer.prev()
    })

    this.elements.thumbnails.querySelectorAll('[data-target]').forEach(thumbButton => {
      const handler = this.setActiveMedia.bind(this, thumbButton.dataset.target)
      thumbButton.addEventListener('click', handler)
      this.thumbnailClickHandlers.push({ element: thumbButton, handler })
    })
  }

  disconnectedCallback() {
    if (this.elements.viewer && this.boundOnSlideChanged) {
      this.elements.viewer.removeEventListener('carouselSlideChanged', this.boundOnSlideChanged)
    }
    if (this.elements.thumbnails) {
      this.thumbnailClickHandlers.forEach(({ element, handler }) => {
        element.removeEventListener('click', handler)
      })
    }
    this.thumbnailClickHandlers = []
  }

  /**
   *  @description when carousel switch, set current active thumbnail
   *  @param {Event} event
   *  @param {number} event.detail.currentElement
   */
  onSlideChanged(event) {
    const thumbnail = this.elements.thumbnails.querySelector(
      `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
    )
    this.setActiveThumbnail(thumbnail)
    this.announceLiveRegion(Number(thumbnail.dataset.mediaPosition) + 1)
  }

  /**
   *  @description set current active thumbnail
   *  @param {string} mediaId
   *  @param {boolean} animation
   */
  setActiveMedia(mediaId, animation = true) {
    this.elements.viewer.destroyMediaContent()
    const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`)
    if (!activeMedia) return

    this.elements.viewer.querySelectorAll('[data-media-id]').forEach(element => {
      element.classList.remove('is-active')
    })
    activeMedia.classList.add('is-active')

    setTimeout(() => {
      const mediaPosition = Number(activeMedia.dataset.mediaPosition)
      if (animation) {
        this.elements.viewer.animateToIndex(mediaPosition + 1, 250, false)
      } else {
        this.elements.viewer.jumpToIndex(mediaPosition + 1)
      }

      const activeMediaRect = activeMedia.getBoundingClientRect()
      //- if image is already in view, do not scroll
      if (activeMediaRect.top > -0.5) return
      const top = activeMediaRect.top + window.scrollY
      window.scrollTo({ top, behavior: 'smooth' })
    }, 0)

    if (!this.elements.thumbnails) return
    const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`)
    if (activeThumbnail) {
      this.setActiveThumbnail(activeThumbnail)
      this.announceLiveRegion(Number(activeThumbnail.dataset.mediaPosition) + 1)
    }
  }

  /**
   *  @description set current active thumbnail
   *  @param {Element} thumbnail
   */
  setActiveThumbnail(thumbnail) {
    if (!this.elements.thumbnails || !thumbnail) return
    this.elements.thumbnails.scrollToSlider(thumbnail)
  }

  /**
   *  @description announce current image position
   *  @param {number} position
   */
  announceLiveRegion(position) {
    if (!this.elements.liveRegion) return
    this.elements.liveRegion.setAttribute('aria-hidden', 'false')
    this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position)
    if (this.liveRegionTimeout) clearTimeout(this.liveRegionTimeout)
    this.liveRegionTimeout = setTimeout(() => {
      this.elements.liveRegion.setAttribute('aria-hidden', 'true')
    }, 2000)
  }
}

/**
 *  @description <carousel-component>
 */
class CarouselComponent extends HTMLElement {
  constructor() {
    super()
    //- constants
    this.DRAG_THRESHOLD = 10
    this.DRAG_SWIPE_THRESHOLD = 0.2
    this.ANIMATION_DURATION = 250

    //- elements
    this.sliderWrapper = this.querySelector('[id^="Carousel-"]')
    this.sliderItems = this.querySelectorAll('[id^="Carousel-Slide-"]')
    this.pagination = this.querySelector('[id^="CarouselPagination"]')
    this.prevButton = this.querySelector('button[name="previous"]')
    this.nextButton = this.querySelector('button[name="next"]')

    //- state
    this.currentIndex = 1
    this.slideWidth = 0
    this.isDragging = false
    this.hasDragged = false
    this.startX = 0
    this.startScrollLeft = 0
    this.animationId = null
    this.totalPages = 0
    this.visibilityObserver = null

    //- save event handler reference for cleanup
    this.boundUpdateWidth = this.updateWidth.bind(this)
    this.boundPrev = throttle(this.prev.bind(this), 250)
    this.boundNext = throttle(this.next.bind(this), 250)
    this.boundPointerDown = this.handlePointerDown.bind(this)
    this.boundPointerMove = this.handlePointerMove.bind(this)
    this.boundPointerUp = this.handlePointerUp.bind(this)
    this.boundPointerCancel = this.handlePointerCancel.bind(this)
  }

  connectedCallback() {
    this.initPages()
    const mediaId = this.dataset.currentMediaId
    if (mediaId) {
      const position = this.querySelector(`[data-media-id="${mediaId}"]`)?.dataset?.mediaPosition
      if (position) this.animateToIndex(Number(position) + 1)
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.boundUpdateWidth)
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
      this.visibilityObserver = null
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.boundPrev)
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.boundNext)
    }
    if (this.sliderWrapper) {
      this.sliderWrapper.removeEventListener('pointerdown', this.boundPointerDown)
      this.sliderWrapper.removeEventListener('pointermove', this.boundPointerMove)
      this.sliderWrapper.removeEventListener('pointerup', this.boundPointerUp)
      this.sliderWrapper.removeEventListener('pointercancel', this.boundPointerCancel)
      this.sliderWrapper.removeEventListener('touchstart', this.boundPointerDown)
      this.sliderWrapper.removeEventListener('touchmove', this.boundPointerMove)
      this.sliderWrapper.removeEventListener('touchend', this.boundPointerUp)
      this.sliderWrapper.removeEventListener('touchcancel', this.boundPointerCancel)
    }
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }
  }

  /**
   *  @description initialize carousel
   */
  initPages() {
    this.setup()
    this.setupDrag()
    window.addEventListener('resize', this.boundUpdateWidth)

    this.dispatchEvent(
      new CustomEvent('carouselSlideChanged', {
        detail: { currentPage: this.currentIndex, currentElement: this.sliderItems[this.currentIndex] }
      })
    )
  }

  /**
   *  @description reset content
   */
  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Carousel-Slide-"]')
    this.initPages()
  }

  /**
   *  @description setup carousel
   */
  setup() {
    this.sliderWrapper = this.querySelector('[id^="Carousel-"]')
    this.sliderItems = this.querySelectorAll('[id^="Carousel-Slide-"]')

    if (!this.sliderWrapper || this.sliderItems.length === 0) return

    this.observeVisibility()

    //- clone first and last item
    const first = this.sliderItems[0].cloneNode(true)
    const last = this.sliderItems[this.sliderItems.length - 1].cloneNode(true)

    this.sliderWrapper.prepend(last)
    this.sliderWrapper.append(first)

    this.sliderItems = Array.from(this.sliderWrapper.children)
    this.totalPages = this.sliderItems.length - 2

    this.updateWidth()
    this.jumpToIndex(this.currentIndex)

    if (this.prevButton) this.prevButton.addEventListener('click', this.boundPrev)

    if (this.nextButton) this.nextButton.addEventListener('click', this.boundNext)
  }

  /**
   *  @description update carousel width
   */
  updateWidth() {
    if (!this.sliderWrapper) return
    const rectWidth = Math.floor(this.sliderWrapper.parentElement.getBoundingClientRect().width)
    this.sliderWrapper.style.width = `${rectWidth}px`
    this.slideWidth = rectWidth
    this.jumpToIndex(this.currentIndex)
  }

  /**
   *  @description update width when component is visible
   */
  observeVisibility() {
    if (this.visibilityObserver) this.visibilityObserver.disconnect()
    if (!('IntersectionObserver' in window) || !this.sliderWrapper) return

    this.visibilityObserver = new IntersectionObserver(entries => {
      const isVisible = entries.some(entry => entry.isIntersecting)
      if (isVisible) this.updateWidth()
    })

    this.visibilityObserver.observe(this)
  }

  /**
   *  @description jump to specified index
   *  @param {number} i
   */
  jumpToIndex(i) {
    this.sliderWrapper.scrollLeft = i * this.slideWidth
    this.updatePagination()
  }

  /**
   *  @description animate to specified index
   *  @param {number} i
   *  @param {number} duration
   *  @param {boolean} dispatchEvent
   */
  animateToIndex(i, duration = this.ANIMATION_DURATION, dispatchEvent = true) {
    cancelAnimationFrame(this.animationId)
    const currentPage = this.getCurrentShownIndex(i)
    if (dispatchEvent) {
      this.dispatchEvent(
        new CustomEvent('carouselSlideChanged', {
          detail: { currentPage, currentElement: this.sliderItems[currentPage] }
        })
      )
    }

    const start = this.sliderWrapper.scrollLeft
    const target = i * this.slideWidth
    const change = target - start
    const startTime = performance.now()

    const animate = now => {
      const progress = Math.min((now - startTime) / duration, 1)
      const ease = progress * (2 - progress)

      this.sliderWrapper.scrollLeft = start + change * ease

      if (progress < 1) {
        this.animationId = requestAnimationFrame(animate)
      } else {
        this.currentIndex = currentPage
        this.jumpToIndex(currentPage)
      }
    }

    this.animationId = requestAnimationFrame(animate)
  }

  /**
   *  @description get current shown index
   *  @param {number} i
   *  @return {number} current shown index
   */
  getCurrentShownIndex(i) {
    if (i === 0) return this.totalPages
    if (i === this.totalPages + 1) return 1
    return i
  }

  /**
   *  @description destroy media content
   */
  destroyMediaContent() {
    const deferredMedia = this.sliderItems[this.currentIndex].querySelector('.component-video')
    if (deferredMedia) deferredMedia.reset()
  }

  /**
   *  @description next page
   */
  next(event) {
    event?.preventDefault()
    this.destroyMediaContent()
    this.animateToIndex(++this.currentIndex)
  }

  /**
   *  @description previous page
   */
  prev(event) {
    event?.preventDefault()
    this.destroyMediaContent()
    this.animateToIndex(--this.currentIndex)
  }

  /**
   *  @description update pagination
   */
  updatePagination() {
    if (!this.pagination) return
    this.pagination.textContent = `${this.currentIndex} / ${this.totalPages}`
  }

  /**
   *  @description setup drag
   */
  setupDrag() {
    if (!this.sliderWrapper) return
    //- Restrict browser default horizontal navigation gestures on touch devices
    this.sliderWrapper.style.touchAction = 'pan-y pinch-zoom'
    //- Pointer Events API (desktop and modern mobile)
    this.sliderWrapper.addEventListener('pointerdown', this.boundPointerDown)
    this.sliderWrapper.addEventListener('pointermove', this.boundPointerMove)
    this.sliderWrapper.addEventListener('pointerup', this.boundPointerUp)
    this.sliderWrapper.addEventListener('pointercancel', this.boundPointerCancel)
    //- Touch Events API (mobile compatibility)
    this.sliderWrapper.addEventListener('touchstart', this.boundPointerDown, { passive: true })
    this.sliderWrapper.addEventListener('touchmove', this.boundPointerMove, { passive: false })
    this.sliderWrapper.addEventListener('touchend', this.boundPointerUp)
    this.sliderWrapper.addEventListener('touchcancel', this.boundPointerCancel)
  }

  /**
   *  @description handle pointer down event
   *  @param {Event} event
   */
  handlePointerDown(event) {
    if (event.button !== 0 && event.pointerType !== 'touch' && !event.touches) return

    this.isDragging = true
    this.hasDragged = false
    const clientX = event.clientX ?? event.touches?.[0]?.clientX ?? 0
    this.startX = clientX
    this.startScrollLeft = this.sliderWrapper.scrollLeft
  }

  /**
   *  @description handle pointer move event
   *  @param {Event} event
   */
  handlePointerMove(event) {
    if (!this.isDragging) return

    const clientX = event.clientX ?? event.touches?.[0]?.clientX ?? 0
    const diff = clientX - this.startX
    const absDiff = Math.abs(diff)

    if (absDiff < this.DRAG_THRESHOLD) return

    if (event.pointerId !== undefined && this.sliderWrapper.setPointerCapture) {
      this.sliderWrapper.setPointerCapture(event.pointerId)
    }

    this.hasDragged = true

    event.preventDefault()

    this.sliderWrapper.scrollLeft = this.startScrollLeft - diff
  }

  /**
   *  @description handle pointer up event
   *  @param {Event} event
   */
  handlePointerUp(event) {
    if (!this.isDragging) return

    const wasDragging = this.hasDragged
    this.isDragging = false

    if (event.pointerId !== undefined && this.sliderWrapper.releasePointerCapture) {
      this.sliderWrapper.releasePointerCapture(event.pointerId)
    }

    if (wasDragging) {
      event.preventDefault()
    }

    const clientX = event.clientX ?? event.changedTouches?.[0]?.clientX ?? 0
    const diff = this.startX - clientX
    const absDiff = Math.abs(diff)

    if (absDiff < this.DRAG_THRESHOLD) {
      this.hasDragged = false
      return
    }

    if (absDiff > this.slideWidth * this.DRAG_SWIPE_THRESHOLD) {
      diff > 0 ? this.next() : this.prev()
    } else {
      this.animateToIndex(this.currentIndex)
    }

    this.hasDragged = false
  }

  /**
   *  @description handle pointer cancel event
   *  @param {Event} event
   */
  handlePointerCancel(event) {
    if (!this.isDragging) return

    const wasDragging = this.hasDragged
    this.isDragging = false
    this.hasDragged = false

    if (event.pointerId !== undefined && this.sliderWrapper.releasePointerCapture) {
      this.sliderWrapper.releasePointerCapture(event.pointerId)
    }

    if (wasDragging) {
      this.animateToIndex(this.currentIndex)
    }
  }

  /**
   *  @description check if element is in view
   *  @param {Element} element
   *  @param {number} offset
   *  @return {boolean}
   */
  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.sliderWrapper.clientWidth + this.sliderWrapper.scrollLeft - offset
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.sliderWrapper.scrollLeft
    )
  }
}

/**
 *  @description <thumbnail-component>
 *  use with <carousel-component>
 */
class ThumbnailComponent extends HTMLElement {
  constructor() {
    super()
    this.DRAG_THRESHOLD = 10

    //- elements
    this.sliderWrapper = this.querySelector('[id^="Thumbnail-"]')
    this.sliderItems = this.querySelectorAll('[id^="Thumbnail-Slide-"]')
    this.prevButton = this.querySelector('button[name="previous"]')
    this.nextButton = this.querySelector('button[name="next"]')
    this.currentIndex = 0
    //- drag state
    this.isDragging = false
    this.startX = 0
    this.startScrollLeft = 0
    this.hasDragged = false

    this.boundPointerDown = this.handlePointerDown.bind(this)
    this.boundPointerMove = this.handlePointerMove.bind(this)
    this.boundPointerUp = this.handlePointerUp.bind(this)
    this.boundPointerCancel = this.handlePointerCancel.bind(this)
  }

  connectedCallback() {
    if (this.sliderItems.length > 0 && this.sliderItems[this.currentIndex]) {
      this.sliderItems[this.currentIndex].setAttribute('aria-current', 'true')
    }

    this.setupDrag()
    this.update()
  }

  disconnectedCallback() {
    if (this.sliderWrapper) {
      this.sliderWrapper.removeEventListener('pointerdown', this.boundPointerDown)
      this.sliderWrapper.removeEventListener('pointermove', this.boundPointerMove)
      this.sliderWrapper.removeEventListener('pointerup', this.boundPointerUp)
      this.sliderWrapper.removeEventListener('pointercancel', this.boundPointerCancel)
      this.sliderWrapper.removeEventListener('touchstart', this.boundPointerDown)
      this.sliderWrapper.removeEventListener('touchmove', this.boundPointerMove)
      this.sliderWrapper.removeEventListener('touchend', this.boundPointerUp)
      this.sliderWrapper.removeEventListener('touchcancel', this.boundPointerCancel)
    }
  }

  /**
   *  @description check left and right switch button state
   */
  update() {
    if (!this.prevButton || !this.nextButton) return
    this.prevButton.toggleAttribute('disabled', this.currentIndex === 0)
    this.nextButton.toggleAttribute('disabled', this.currentIndex === this.sliderItems.length - 1)
  }

  /**
   *  @description scroll to specified thumbnail
   *  @param {Element} slider
   */
  scrollToSlider(slider) {
    if (!slider || this.currentIndex === Number(slider.dataset.mediaPosition)) return
    if (this.sliderItems[this.currentIndex]) this.sliderItems[this.currentIndex].removeAttribute('aria-current')

    slider.setAttribute('aria-current', 'true')
    this.currentIndex = Number(slider.dataset.mediaPosition)
    this.sliderWrapper.scrollTo({ left: slider.offsetLeft, behavior: 'smooth' })
    this.update()
  }

  /**
   *  @description drag setup
   */
  setupDrag() {
    if (!this.sliderWrapper) return
    //- Restrict browser default horizontal navigation gestures on touch devices
    this.sliderWrapper.style.touchAction = 'pan-y'
    this.sliderWrapper.addEventListener('pointerdown', this.boundPointerDown)
    this.sliderWrapper.addEventListener('pointermove', this.boundPointerMove)
    this.sliderWrapper.addEventListener('pointerup', this.boundPointerUp)
    this.sliderWrapper.addEventListener('pointercancel', this.boundPointerCancel)
    this.sliderWrapper.addEventListener('touchstart', this.boundPointerDown, { passive: true })
    this.sliderWrapper.addEventListener('touchmove', this.boundPointerMove, { passive: false })
    this.sliderWrapper.addEventListener('touchend', this.boundPointerUp)
    this.sliderWrapper.addEventListener('touchcancel', this.boundPointerCancel)
  }

  /**
   *  @description handle pointer down event
   *  @param {Event} event
   */
  handlePointerDown(event) {
    this.isDragging = true
    this.hasDragged = false
    const clientX = event.clientX ?? event.touches?.[0]?.clientX ?? 0
    this.startX = clientX
    this.startScrollLeft = this.sliderWrapper.scrollLeft
  }

  /**
   *  @description handle pointer move event
   *  @param {Event} event
   */
  handlePointerMove(event) {
    if (!this.isDragging) return

    const clientX = event.clientX ?? event.touches?.[0]?.clientX ?? 0
    const diff = clientX - this.startX
    const absDiff = Math.abs(diff)

    if (absDiff < this.DRAG_THRESHOLD) return
    if (event.pointerId !== undefined) {
      this.sliderWrapper.setPointerCapture(event.pointerId)
    }

    this.hasDragged = true
    this.sliderWrapper.style.cursor = 'grabbing'

    event.preventDefault()

    this.sliderWrapper.scrollLeft = this.startScrollLeft - diff
  }

  /**
   *  @description handle pointer up event
   *  @param {Event} event
   */
  handlePointerUp(event) {
    if (!this.isDragging) return

    const wasDragging = this.hasDragged
    this.isDragging = false
    this.sliderWrapper.style.cursor = ''

    if (wasDragging) {
      if (event.pointerId !== undefined) {
        this.sliderWrapper.releasePointerCapture(event.pointerId)
      }
      event.preventDefault()
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.hasDragged = false
        }, 0)
      })
    } else {
      this.hasDragged = false
    }
  }

  /**
   *  @description handle pointer cancel event
   *  @param {Event} event
   */
  handlePointerCancel(event) {
    if (!this.isDragging) return
    this.isDragging = false
    this.hasDragged = false
    if (event.pointerId !== undefined) {
      this.sliderWrapper.releasePointerCapture(event.pointerId)
    }
    this.sliderWrapper.style.cursor = ''
  }
}

class ProductMediaModalOpener extends HTMLElement {
  constructor() {
    super()

    const button = this.querySelector('[id^="Product-Media-Image-"]')

    if (!button) return
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'))
      if (modal) modal.show(button)
    })
  }
}
customElements.define('product-media-modal-opener', ProductMediaModalOpener)

if (!customElements.get('product-media-modal')) {
  customElements.define(
    'product-media-modal',
    class ProductMediaModal extends HTMLElement {
      constructor() {
        super()
        this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', this.hide.bind(this, false))
        this.addEventListener('keyup', event => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide()
        })
        if (this.classList.contains('media-modal')) {
          this.addEventListener('pointerup', event => {
            if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-media-modal'))
              this.hide()
          })
        } else {
          this.addEventListener('click', event => {
            if (event.target === this) this.hide()
          })
        }
      }

      connectedCallback() {
        if (this.moved) return
        this.moved = true
      }

      hide() {
        document.body.classList.remove('overflow-hidden')
        document.body.dispatchEvent(new CustomEvent('modalClosed'))
        this.removeAttribute('open')
        removeTrapFocus(this.openedBy)
      }

      show(opener) {
        this.openedBy = opener
        const popup = this.querySelector('.template-popup')
        document.body.classList.add('overflow-hidden')
        this.setAttribute('open', '')
        if (popup) popup.loadContent()
        trapFocus(this, this.querySelector('[role="dialog"]'))
        const { start } = opener.dataset
        this.showActiveMedia(start)
      }

      showActiveMedia(start) {
        const splide = this.querySelector('[id^="Product-Media-Image-"]')
        if (!splide.slider) return
        splide.slider.go(Number(start) - 1)
      }
    }
  )
}

/**
 *  @description <product-media-tabs>
 */
class ProductMediaTabs extends HTMLElement {
  constructor() {
    super()
    this.tabsList = this.querySelector('[id^="ProductMediaTabsList-"]')
    this.modelViewer = this.querySelector('.product-model-viewer')
    this.modelProgressRoot = this.modelViewer?.querySelector('[data-model-progress]')
    this.modelProgressValue = this.modelViewer?.querySelector('[data-model-progress-value]')
    this.modelFullscreenTarget = this.querySelector('.product-media-tabpanel--model')
    this.modelFullscreenButton = this.querySelector('.product-model-panel__fullscreen')

    if (!this.tabsList) return

    this.boundTabClick = this.handleTabClick.bind(this)
    this.tabsList.querySelectorAll('.product-media-tab').forEach(tab => {
      tab.addEventListener('click', this.boundTabClick)
    })

    if (this.modelViewer && this.modelProgressRoot && this.modelProgressValue && this.modelFullscreenTarget) {
      this.modelLastPercent = -1
      this.modelIsFullscreenActive = null
      this.boundModelProgress = this.handleModelProgress.bind(this)
      this.boundModelLoad = this.handleModelLoad.bind(this)
      this.boundModelFullscreenClick = this.handleModelFullscreenClick.bind(this)
      this.boundModelFullscreenChange = this.handleModelFullscreenChange.bind(this)

      this.initModelViewer()
    }
  }

  disconnectedCallback() {
    if (this.tabsList) {
      this.tabsList.querySelectorAll('.product-media-tab').forEach(tab => {
        tab.removeEventListener('click', this.boundTabClick)
      })
    }

    this.cleanupModelViewer()
  }

  /**
   *  @description handle tab click event
   *  @param {Event} event
   */
  handleTabClick(event) {
    const tab = event.currentTarget
    const tabpanelId = tab.getAttribute('aria-controls')

    if (!tabpanelId) return

    //- update tab state
    this.tabsList.querySelectorAll('.product-media-tab').forEach(t => {
      t.classList.remove('product-media-tab--active')
      t.setAttribute('aria-selected', 'false')
    })
    tab.classList.add('product-media-tab--active')
    tab.setAttribute('aria-selected', 'true')

    //- show/hide corresponding tabpanel
    const tabPanels = this.querySelectorAll('.product-media-tabpanel')
    tabPanels.forEach(panel => {
      if (panel.id === tabpanelId) {
        panel.classList.add('product-media-tabpanel--active')
        panel.removeAttribute('hidden')
      } else {
        panel.classList.remove('product-media-tabpanel--active')
        panel.setAttribute('hidden', '')
      }
    })
  }

  initModelViewer() {
    // 初始化时先同步一次状态，兼容模型已缓存完成
    // 或页面已处于 fullscreen 的场景，避免等待异步事件后才更新 UI。
    this.updateModelProgress(this.modelViewer.hasAttribute('loaded') || this.modelViewer.loaded === true ? 1 : 0)
    this.updateModelFullscreenButton(document.fullscreenElement === this.modelFullscreenTarget)

    this.modelViewer.addEventListener('progress', this.boundModelProgress)
    this.modelViewer.addEventListener('load', this.boundModelLoad, { once: true })
    this.modelFullscreenButton?.addEventListener('click', this.boundModelFullscreenClick)
    document.addEventListener('fullscreenchange', this.boundModelFullscreenChange)
  }

  cleanupModelViewer() {
    if (!this.modelViewer) return

    this.modelViewer.removeEventListener('progress', this.boundModelProgress)
    this.modelViewer.removeEventListener('load', this.boundModelLoad)
    this.modelFullscreenButton?.removeEventListener('click', this.boundModelFullscreenClick)
    document.removeEventListener('fullscreenchange', this.boundModelFullscreenChange)
  }

  handleModelProgress(event) {
    const progress = event.detail && typeof event.detail.totalProgress === 'number' ? event.detail.totalProgress : 0
    this.updateModelProgress(progress)
  }

  handleModelLoad() {
    this.modelFullscreenButton?.classList.add('product-model-panel__fullscreen--visible')
    this.updateModelProgress(1)
  }

  handleModelFullscreenClick() {
    if (document.fullscreenElement === this.modelFullscreenTarget) {
      document.exitFullscreen()
      return
    }

    if (typeof this.modelFullscreenTarget.requestFullscreen === 'function') {
      this.modelFullscreenTarget.requestFullscreen()
    }
  }

  handleModelFullscreenChange() {
    this.updateModelFullscreenButton(document.fullscreenElement === this.modelFullscreenTarget)
  }

  updateModelProgress(progress) {
    const normalizedProgress = Number.isFinite(progress) ? progress : 0
    const percent = Math.max(0, Math.min(100, Math.round(normalizedProgress * 100)))

    // model-viewer 的 progress 事件触发会比较频繁，这里跳过重复百分比，减少 DOM 写入。
    if (percent === this.modelLastPercent) return

    this.modelLastPercent = percent
    this.modelProgressValue.textContent = `${percent}`
    this.modelProgressRoot.classList.toggle('hidden', percent >= 100)
  }

  updateModelFullscreenButton(isFullscreen) {
    // 只有 fullscreen 状态真的变化时才更新 ARIA 和 class，避免无意义的重复写入。
    if (!this.modelFullscreenButton || isFullscreen === this.modelIsFullscreenActive) return

    this.modelIsFullscreenActive = isFullscreen
    const ariaLabel = isFullscreen ? 'Exit 3D model fullscreen' : 'Open 3D model fullscreen'

    this.modelFullscreenButton.classList.toggle('product-model-panel__fullscreen--active', isFullscreen)
    this.modelFullscreenButton.setAttribute('aria-label', ariaLabel)
    this.modelFullscreenButton.setAttribute('title', ariaLabel)
    this.modelFullscreenButton.setAttribute('aria-pressed', isFullscreen ? 'true' : 'false')
  }
}

if (!customElements.get('media-carousel')) {
  customElements.define('media-carousel', MediaCarousel)
  customElements.define('carousel-component', CarouselComponent)
  customElements.define('thumbnail-component', ThumbnailComponent)
}

if (!customElements.get('product-media-tabs')) {
  customElements.define('product-media-tabs', ProductMediaTabs)
}
